import { Injectable } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { BehaviorSubject, filter } from 'rxjs';

export interface Breadcrumb {
    label: string;
    url: string;
}

@Injectable({
    providedIn: 'root'
})
export class BreadcrumbService {
    private breadcrumbs = new BehaviorSubject<Breadcrumb[]>([]);
    breadcrumbs$ = this.breadcrumbs.asObservable();

    constructor(private router: Router, private activatedRoute: ActivatedRoute) {
        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => {
                const breadcrumbs = this.createBreadcrumbs(this.activatedRoute.root);
                this.breadcrumbs.next(breadcrumbs);
            });
    }

    private createBreadcrumbs(route: ActivatedRoute, url: string = '', breadcrumbs: Breadcrumb[] = []): Breadcrumb[] {
        const children: ActivatedRoute[] = route.children;

        if (children.length === 0) {
            return breadcrumbs;
        }

        for (const child of children) {
            const routeURL: string = child.snapshot.url.map(segment => segment.path).join('/');
            const fullUrl = routeURL ? `${url}/${routeURL}` : url;

            const label = this.getBreadcrumbLabel(child, routeURL);
            // Skip empty path routes that duplicate the parent's title
            if (label && !(routeURL === '' && breadcrumbs.length > 0 && breadcrumbs[breadcrumbs.length - 1].label === label)) {
                breadcrumbs.push({ label, url: fullUrl });
            }

            return this.createBreadcrumbs(child, fullUrl, breadcrumbs);
        }

        return breadcrumbs;
    }

    private getBreadcrumbLabel(route: ActivatedRoute, routeURL: string): string | null {
        // Use data.title if available
        const title = route.snapshot.data['title'];
        if (title) {
            return title;
        }

        // Skip empty paths without title
        if (routeURL === '') {
            return null;
        }

        // Capitalize routeURL as fallback
        return this.capitalize(routeURL);
    }

    private capitalize(str: string): string {
        return str
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }
}